import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import type { IncomingInvoiceDetail, Nullable } from "@msuite/utils/types";

/** Props Interface */
interface IncomingInvoiceSourceCardProps {
	incomingInvoice: Nullable<IncomingInvoiceDetail>;
}

export function IncomingInvoiceSourceCard({ incomingInvoice }: IncomingInvoiceSourceCardProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const email = incomingInvoice?.sourceEmail ?? null;
	const sender = email?.fromName || email?.fromAddress;
	const recipientAddress = [
		[incomingInvoice?.recipientStreet, incomingInvoice?.recipientHouseNumber]
			.filter(Boolean)
			.join(" "),
		[incomingInvoice?.recipientZipCode, incomingInvoice?.recipientCity].filter(Boolean).join(" "),
	].filter(Boolean);

	/** Render */
	return (
		<ContentCard
			title={t("incoming_invoices.source")}
			icon={icon("Mail")}
		>
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.recipient")}</DataGrid.Label>
					<DataGrid.Value className="v-stack items-start gap-0">
						<span>{guardString(incomingInvoice?.recipientName)}</span>
						{recipientAddress.map((line) => (
							<span key={line}>{line}</span>
						))}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Divider />
				<DataGrid.Row>
					<DataGrid.Label>{t("mailboxes.from")}</DataGrid.Label>
					<DataGrid.Value>{guardString(sender)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("mailboxes.subject")}</DataGrid.Label>
					<DataGrid.Value>{guardString(email?.subject)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.email_arrived_at")}</DataGrid.Label>
					<DataGrid.Value>
						{email ? moment(email.receivedAt).longFormat() : guardString(null)}
					</DataGrid.Value>
				</DataGrid.Row>
				{incomingInvoice?.extractionError && (
					<DataGrid.Row>
						<DataGrid.Label>{t("incoming_invoices.extraction_error")}</DataGrid.Label>
						<DataGrid.Value>{incomingInvoice.extractionError}</DataGrid.Value>
					</DataGrid.Row>
				)}
			</DataGrid>
		</ContentCard>
	);
}
